import { MouseEvent, SyntheticEvent, useEffect, useRef, useState } from 'react';

import { useIsAllowedPermission } from '@mint-lib/profile-helpers';

import { useUpdateMediaPlanRowLockedMutation } from '../../../../../../api/mediaPlansApiSlice.js';
import { useCellValueUpdate } from '../../../../../../hooks/useCellValueUpdate.js';
import { useRefetchBuyingPlanInfo } from '../../../../../../hooks/useGetBuyingPlanData.js';
import { TRACKING_EVENTS } from '../../../../../../types/performanceTracking.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../../types.js';
import { GoalOption, SavedGoal } from './types.js';
import {
  getDecimalLimit,
  getInitialGoalOption,
  getInitialGoalValue,
  prepareValue,
} from './utils.js';

type UseEditableFieldArgs = {
  value: SavedGoal | null;
  column: BuilderTableColumn;
  row: BuilderTableRow;
};

const useEditableGoalField = ({ value, column, row }: UseEditableFieldArgs) => {
  const [popoverAnchorElement, setPopoverAnchorElement] =
    useState<HTMLElement | null>(null);
  const [selectedGoalOption, setSelectedGoalOption] =
    useState<GoalOption | null>(getInitialGoalOption(value));
  const [selectedGoalValue, setSelectedGoalValue] = useState<number | null>(
    getInitialGoalValue(value),
  );

  const {
    isLockedForRecommendations,
    isBuyingPlanLocked,
    isCampaignCurrencyMode,
    isPredictivePlanningMode,
    isArchivedVersion,
  } = row.original;

  const [isRowLocked, setIsRowLocked] = useState<boolean>(
    !!isLockedForRecommendations,
  );

  //TODO: REFACTOR IT!!!
  // These 5 refs are used for having up-to-date values. The values are used in key down event and all connected methods.
  const selectedGoalOptionRef = useRef<GoalOption | null>(
    getInitialGoalOption(value),
  );
  const selectedGoalValueRef = useRef<number | null>(
    getInitialGoalValue(value),
  );
  const isRowLockedRef = useRef<boolean>(!!isLockedForRecommendations);
  const isEnterKeyDownSkippedRef = useRef<boolean>(false);

  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');
  const isReadOnlyField =
    !isAllowed ||
    isPredictivePlanningMode ||
    isArchivedVersion ||
    isCampaignCurrencyMode ||
    isBuyingPlanLocked;

  const [updateRowLocked] = useUpdateMediaPlanRowLockedMutation();
  const refetchBuyingPlanInfo = useRefetchBuyingPlanInfo();
  const { startMeasurePerformance } = column;

  const goalValueDecimalLimit = getDecimalLimit(
    selectedGoalOptionRef.current?.valueType,
  );

  const currentPreparedValue = prepareValue(
    value?.goalValue ?? 0,
    goalValueDecimalLimit,
  );

  const { updateCellValue } = useCellValueUpdate({ column, row });

  const handleSaveGoal = async (goal: SavedGoal | null) => {
    startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);

    const isDifferentValue =
      goal?.goalValue !== value?.goalValue ||
      goal?.metricId !== value?.metricId;

    if (isDifferentValue) {
      const newGoal = await updateCellValue({
        currentValue: value,
        newValue: goal,
      });

      setSelectedGoalOption(getInitialGoalOption(newGoal));
      setSelectedGoalValue(getInitialGoalValue(newGoal));
      selectedGoalOptionRef.current = getInitialGoalOption(newGoal);
      selectedGoalValueRef.current = getInitialGoalValue(newGoal);
    }
  };

  const handleChangeRowLock = () => {
    isRowLockedRef.current = !isRowLocked;
    setIsRowLocked(!isRowLocked);
  };

  const handleRowLock = async () => {
    if (!!isLockedForRecommendations === isRowLockedRef.current) {
      return;
    }

    await updateRowLocked({
      rowId: row.original.id,
      isLockedForRecommendations: isRowLockedRef.current,
    });

    const valueToSave = prepareValue(
      selectedGoalValueRef.current ?? 0,
      getDecimalLimit(selectedGoalOptionRef.current?.valueType),
    );

    const shouldSkipSaveOperation =
      (value && !selectedGoalOptionRef.current) ||
      currentPreparedValue !== valueToSave ||
      selectedGoalOptionRef.current?.id !== value?.metricId;

    if (shouldSkipSaveOperation) {
      return;
    }

    refetchBuyingPlanInfo({ throwOnError: false });
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isAllowed) {
      return;
    }
    startMeasurePerformance(TRACKING_EVENTS.VALUE_INSERTION);
    setPopoverAnchorElement(event.currentTarget);
  };

  const handlePopoverClose = async () => {
    setPopoverAnchorElement(null);

    await handleRowLock();

    if (!value && !selectedGoalOptionRef.current) {
      return;
    }

    if (!selectedGoalValueRef.current && !selectedGoalOptionRef.current) {
      handleSaveGoal(null);
      return;
    }

    const valueToSave = prepareValue(
      selectedGoalValueRef.current ?? 0,
      getDecimalLimit(selectedGoalOptionRef.current?.valueType),
    );

    if (
      !value ||
      currentPreparedValue !== valueToSave ||
      selectedGoalOptionRef.current?.id !== value.metricId
    ) {
      handleSaveGoal({
        goalValue: valueToSave,
        metricId: selectedGoalOptionRef.current!.id,
        metricLabel: selectedGoalOptionRef.current!.label,
        valueType: selectedGoalOptionRef.current!.valueType,
      });
    }
  };

  const handleChangeGoalValue = (goalValue: number | null) => {
    selectedGoalValueRef.current = goalValue;
    setSelectedGoalValue(goalValue);
  };

  const handleClearGoal = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    await handleSaveGoal(null);
    setSelectedGoalValue(null);
  };

  const handleChangeGoalOption = (
    event: SyntheticEvent,
    option: GoalOption | null,
  ) => {
    selectedGoalOptionRef.current = option;
    selectedGoalValueRef.current = null;
    setSelectedGoalOption(option);
    setSelectedGoalValue(null);
  };

  const handleEnableEnterKeyDownSkip = () => {
    isEnterKeyDownSkippedRef.current = true;
  };

  const handleDisableEnterKeyDownSkip = () => {
    // setTimeout is used for queueing a macro task
    setTimeout(() => {
      isEnterKeyDownSkippedRef.current = false;
    });
  };

  const handleKeyDown = (e: globalThis.KeyboardEvent) => {
    if (e.key === 'Enter' && !isEnterKeyDownSkippedRef.current) {
      handlePopoverClose();
    }
  };

  useEffect(() => {
    if (popoverAnchorElement) {
      globalThis.addEventListener('keydown', handleKeyDown);

      return () => {
        globalThis.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [popoverAnchorElement]);

  return {
    isReadOnlyField,
    isRowLocked,
    selectedGoalOption,
    selectedGoalValue,
    goalValueDecimalLimit,
    popoverAnchorElement,
    handleChangeRowLock,
    handlePopoverOpen,
    handlePopoverClose,
    handleClearGoal,
    handleChangeGoalOption,
    handleChangeGoalValue,
    handleEnableEnterKeyDownSkip,
    handleDisableEnterKeyDownSkip,
  };
};

export default useEditableGoalField;
